import { Fragment, useEffect, useMemo, useState } from "react";
import { endOfMonth, format, startOfMonth, subMonths } from "date-fns";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatEnrichmentMoney } from "@/lib/enrichmentMoney";
import { EnrichmentPayoutSummary } from "./EnrichmentPayoutSummary";
import { EnrichmentHistory } from "./EnrichmentHistory";
import {
  getEnrichmentTeamStats,
  type EnrichmentStatsPeriod,
  type EnrichmentTeamStatsParams,
  type EnrichmentTeamStatsResponse,
} from "@/api/enrichment";

interface EnrichmentTeamStatsProps {
  slug: string;
}

/** The window being asked for. A rolling `period` and a calendar month are
 *  two different questions, and the backend answers them through different
 *  parameters — `start`/`end` win over `period` — so the selector models
 *  them as different states rather than one list of strings. */
type StatsWindow =
  | { kind: "period"; period: EnrichmentStatsPeriod }
  | { kind: "month"; month: Date };

/** How many past months the calendar picker offers. Payouts are settled
 *  on the month just closed, so a year of history is plenty. */
const MONTH_CHOICES = 12;

const PERIOD_OPTIONS: Array<{ period: EnrichmentStatsPeriod; label: string }> = [
  { period: "week", label: "Last 7 days" },
  // Labelled for what it is: `period=month` is a rolling 30 days, and
  // calling it "This month" is exactly the confusion the calendar picker
  // next to it exists to remove.
  { period: "month", label: "Last 30 days" },
  { period: "all", label: "All time" },
];

function toParams(window: StatsWindow): EnrichmentTeamStatsParams {
  if (window.kind === "period") return { period: window.period };
  // Inclusive bounds, and `end` covers its whole day server-side — so the
  // last day of the month is in the month it belongs to.
  return {
    start: format(startOfMonth(window.month), "yyyy-MM-dd"),
    end: format(endOfMonth(window.month), "yyyy-MM-dd"),
  };
}

/** Seconds as the operator reads them: "45s", "2m 05s". */
function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${String(rest).padStart(2, "0")}s`;
}

/** Per-operator enrichment counts, payout and average time.
 *
 *  Deliberately carries no permission logic. `enrichment.view_stats` is
 *  what the endpoint requires, and everything past that — whether the
 *  answer covers the team or only the caller — is the server's decision,
 *  reported back as `scope`. Reproducing that rule here would give us a
 *  second copy of it to keep in step. */
export function EnrichmentTeamStats({ slug }: EnrichmentTeamStatsProps) {
  const [window, setWindow] = useState<StatsWindow>({
    kind: "period",
    period: "month",
  });
  const [data, setData] = useState<EnrichmentTeamStatsResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: MONTH_CHOICES }, (_, i) =>
      startOfMonth(subMonths(now, i)),
    );
  }, []);

  const params = useMemo(() => toParams(window), [window]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setData(null);
    getEnrichmentTeamStats(slug, params)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        console.warn("[Enrichment] team stats load failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, params]);

  const selectedMonth =
    window.kind === "month" ? format(window.month, "yyyy-MM") : "";

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {/* Straight from `scope`, never from the row count: a
              * one-person team is "team" with one line, and a lone
              * operator is "self" with one line. Same shape, opposite
              * meaning. */}
            {data?.scope === "self" ? "My statistics" : "Team"}
          </h2>
          <p className="text-sm text-slate-500">
            {data ? describeWindow(data) : "Enrichment output and payout"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.period}
                type="button"
                onClick={() =>
                  setWindow({ kind: "period", period: option.period })
                }
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
                  window.kind === "period" && window.period === option.period
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Payouts are settled per calendar month, which no `period`
            * value expresses — this is what `start`/`end` are for. */}
          <select
            aria-label="Calendar month"
            value={selectedMonth}
            onChange={(e) => {
              const [year, month] = e.target.value.split("-").map(Number);
              if (!year || !month) return;
              setWindow({ kind: "month", month: new Date(year, month - 1, 1) });
            }}
            className={cn(
              "h-8 rounded-lg border px-2 text-xs font-medium transition-colors",
              window.kind === "month"
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-slate-200 bg-white text-slate-500 hover:text-slate-700",
            )}
          >
            <option value="" disabled>
              Calendar month
            </option>
            {months.map((m) => (
              <option key={format(m, "yyyy-MM")} value={format(m, "yyyy-MM")}>
                {format(m, "MMMM yyyy")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {status === "error" ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Couldn&rsquo;t load the operator statistics. Try another period, or
          refresh the page.
        </div>
      ) : status === "loading" && !data ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : data ? (
        <>
        <EnrichmentPayoutSummary total={data.total} currency={data.currency} scope={data.scope} />
        <div
          className={cn(
            "overflow-x-auto rounded-plane bg-white shadow-plane transition-opacity",
            status === "loading" && "opacity-60",
          )}
        >
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3 font-medium">Operator</th>
                <th className="px-4 py-3 text-right font-medium">Enriched</th>
                <th className="px-4 py-3 text-right font-medium">
                  Corrections
                  {/* Counted, and worth zero by contract. Shown in its own
                    * column rather than folded into enriched or into the
                    * payout. */}
                  <span className="ml-1 normal-case text-slate-300">
                    (unpaid)
                  </span>
                </th>
                <th className="px-4 py-3 text-right font-medium">Not found</th>
                <th className="px-4 py-3 text-right font-medium">Avg time</th>
                <th className="px-4 py-3 text-right font-medium">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {data.operators.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-500"
                  >
                    Nobody logged enrichment work in this window.
                    <span className="mt-1 block text-xs text-slate-400">
                      The list holds whoever worked, not the roster — an
                      empty one means an empty window.
                    </span>
                  </td>
                </tr>
              ) : (
                /* Delivered sorted by earnings desc, then name. Kept in
                 * that order rather than re-sorted here. */
                data.operators.map((operator) => (
                  <Fragment key={operator.user_id}>
                    <tr
                      className="border-b border-slate-50 last:border-0"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">
                          {operator.full_name ?? operator.email ?? "—"}
                        </div>
                        {operator.full_name && operator.email && (
                          <div className="text-xs text-slate-400">
                            {operator.email}
                          </div>
                        )}
                        {data.end_at ? (
                          <button
                            type="button"
                            aria-expanded={expanded.has(operator.user_id)}
                            aria-controls={`enrichment-history-${operator.user_id}`}
                            disabled={status === "loading"}
                            onClick={() => setExpanded((previous) => {
                              const next = new Set(previous);
                              if (next.has(operator.user_id)) next.delete(operator.user_id);
                              else next.add(operator.user_id);
                              return next;
                            })}
                            className="mt-1 text-xs font-medium text-indigo-600 hover:underline disabled:opacity-50"
                          >
                            {expanded.has(operator.user_id) ? "Hide prospect details" : "Show prospect details"}
                          </button>
                        ) : (
                          <p className="mt-1 text-xs text-slate-400">Prospect details require the backend update.</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                        {operator.enriched}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-amber-600">
                        {operator.corrections}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-400">
                        {operator.not_found}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                        {formatDuration(operator.avg_time_seconds)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums text-emerald-600">
                        {formatEnrichmentMoney(operator.earnings, data.currency)}
                      </td>
                    </tr>
                    {data.end_at && expanded.has(operator.user_id) && (
                      <tr id={`enrichment-history-${operator.user_id}`}>
                        <td colSpan={6} className="bg-slate-50/60">
                          <EnrichmentHistory
                            key={`${slug}:${operator.user_id}:${data.start_at}:${data.end_at}`}
                            slug={slug}
                            userId={operator.user_id}
                            startAt={data.start_at ?? null}
                            endAt={data.end_at}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
            <tfoot>
              {/* Rendered whether or not anybody worked — the backend
                * sends a complete line of zeros for the empty window, so
                * the table keeps its footer instead of losing it exactly
                * when someone is checking why it is empty.
                *
                * Every number here is the server's. `avg_time_seconds`
                * especially: it is weighted over every paid record, not
                * the mean of the column above it, so re-deriving it from
                * these rows would print a different number. */}
              <tr className="border-t border-slate-200 bg-slate-50/60 font-medium">
                <td className="px-4 py-3 text-slate-700">
                  Total
                  <span className="ml-1.5 text-xs font-normal text-slate-400">
                    {data.total.operators} operator
                    {data.total.operators === 1 ? "" : "s"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                  {data.total.enriched}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-700">
                  {data.total.corrections}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                  {data.total.not_found}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                  {formatDuration(data.total.avg_time_seconds)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                  {formatEnrichmentMoney(data.total.earnings, data.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        </>
      ) : null}

      {data && (
        <p className="text-xs text-slate-400">
          Corrections are rework of earlier enrichment: they count as
          activity and pay nothing. The average time covers paid records
          only, weighted across all of them.
        </p>
      )}
    </div>
  );
}

/** The window the server actually applied, in its own words — `start` and
 *  `end` come back on every response, and they are what `period` resolved
 *  to (or the calendar month that overrode it). */
function describeWindow(data: EnrichmentTeamStatsResponse): string {
  if (!data.start) return "All time";
  const start = new Date(`${data.start}T00:00:00`);
  const end = new Date(`${data.end}T00:00:00`);
  return `${format(start, "MMM d, yyyy")} — ${format(end, "MMM d, yyyy")}`;
}
